import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Swal from "sweetalert2";
import "../modeSelect/modeSelect.css";
import "./sessionReport.css";
import {
  fetchSession,
  selectSession,
  selectActivities,
} from "../../sessionBuilderSlice";
import { selectAdminToken } from "../../sessionSlice";
import {
  liveId,
  resetLiveSession,
  fetchAllPlayedData,
} from "../../../functions/liveSession";

const TYPE_LABEL = {
  quiz: "Quiz",
  poll: "Poll",
  wordcloud: "Word Cloud",
  pindrop: "Pin Drop",
};

// Quote a CSV cell when it contains comma, quote or newline; double inner quotes.
function csvCell(val) {
  const s = String(val ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csvRow(cells) {
  return cells.map(csvCell).join(",");
}

const SessionReport = () => {
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const activities = useSelector(selectActivities);
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector((s) => s.session?.organizationId);
  const sessionId = useSelector((s) => s.session?.sessionId);
  const currentTheme = useSelector(
    (s) => s.theme?.currentTheme || s.theme?.data?.themename || null
  );
  const [resetting, setResetting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    dispatch(fetchSession());
  }, [dispatch]);

  // Load each activity's content (questions/options/prompts) so the report
  // can show readable question text and option labels instead of raw indexes.
  const loadContentMap = async () => {
    const map = {};
    await Promise.all(
      activities.map(async (a) => {
        try {
          const res = await fetch(`${API}/pp2/getActivityContent`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({ activity_id: a.id, currentTheme }),
          });
          const json = await res.json();
          map[a.id] = json.success ? json.data.content || [] : [];
        } catch (e) {
          map[a.id] = [];
        }
      })
    );
    return map;
  };

  const buildCsv = (played, contentMap) => {
    const actById = {};
    activities.forEach((a, i) => {
      actById[a.id] = { ...a, order: i + 1 };
    });
    const actLabel = (activityId) => {
      const a = actById[activityId];
      if (!a) return `Activity ${activityId}`;
      return `#${a.order} ${a.title || "(untitled)"} [${TYPE_LABEL[a.type] || a.type}]`;
    };
    // content rows are ordered as stored; qIdx indexes into that array.
    const questionText = (activityId, qIdx) => {
      const c = (contentMap[activityId] || [])[qIdx];
      if (!c) return `Q${qIdx + 1}`;
      return c.question_text || c.prompt || `Q${qIdx + 1}`;
    };
    const optionText = (activityId, qIdx, optionIndex) => {
      const c = (contentMap[activityId] || [])[qIdx];
      if (c && Array.isArray(c.options) && c.options[optionIndex] != null) {
        return c.options[optionIndex];
      }
      return `Option ${optionIndex + 1}`;
    };

    const ms = (v) => (v ? `${(v / 1000).toFixed(1)}s` : "");
    const lines = [];
    const section = (title) => {
      if (lines.length) lines.push("");
      lines.push(csvRow([title]));
    };

    // --- Session summary ---
    section("SESSION SUMMARY");
    lines.push(csvRow(["Title", session?.title || ""]));
    lines.push(csvRow(["Mode", (session?.mode ?? "live") === "live" ? "Live" : "Self-Paced"]));
    lines.push(csvRow(["Scoring", (session?.scoring_mode ?? "custom") === "auto" ? "Auto (time-based)" : "Custom (fixed)"]));
    lines.push(csvRow(["Points per correct", (session?.scoring_mode ?? "custom") === "auto" ? "Varies (50-100, time-based)" : session?.point_value ?? ""]));
    lines.push(csvRow(["Activities", activities.length]));
    lines.push(csvRow(["Participants", (played.participants || []).length]));
    lines.push(csvRow(["Exported at", new Date().toLocaleString()]));

    // --- Leaderboard (participants) ---
    section("PARTICIPANTS / LEADERBOARD");
    lines.push(
      csvRow(["Rank", "Name", "User ID", "Points", "Correct", "Answered", "Total Time"])
    );
    const parts = [...(played.participants || [])].sort(
      (a, b) =>
        (b.points || 0) - (a.points || 0) ||
        (b.correctCount || 0) - (a.correctCount || 0) ||
        (a.totalTimeMs || 0) - (b.totalTimeMs || 0)
    );
    parts.forEach((p, i) => {
      lines.push(
        csvRow([
          i + 1,
          p.name || p.userId,
          p.userId,
          p.points || 0,
          p.correctCount || 0,
          p.answeredCount || 0,
          ms(p.totalTimeMs),
        ])
      );
    });

    // --- Quiz responses ---
    if ((played.responses || []).length) {
      section("QUIZ RESPONSES");
      lines.push(
        csvRow(["Activity", "Question", "Name", "User ID", "Answer", "Correct", "Points", "Time"])
      );
      [...played.responses]
        .sort((a, b) => (a.submittedAt || 0) - (b.submittedAt || 0))
        .forEach((r) => {
          lines.push(
            csvRow([
              actLabel(r.activityId),
              questionText(r.activityId, r.qIdx),
              r.name || r.userId,
              r.userId,
              optionText(r.activityId, r.qIdx, r.optionIndex),
              r.correct ? "Yes" : "No",
              r.points || 0,
              ms(r.timeMs),
            ])
          );
        });
    }

    // --- Poll votes ---
    if ((played.votes || []).length) {
      section("POLL VOTES");
      lines.push(csvRow(["Activity", "Question", "Name", "User ID", "Choice"]));
      [...played.votes]
        .sort((a, b) => (a.submittedAt || 0) - (b.submittedAt || 0))
        .forEach((v) => {
          lines.push(
            csvRow([
              actLabel(v.activityId),
              questionText(v.activityId, v.qIdx),
              v.name || v.userId,
              v.userId,
              optionText(v.activityId, v.qIdx, v.optionIndex),
            ])
          );
        });
    }

    // --- Word cloud ---
    const words = (played.words || []).filter((w) => !w.removed);
    if (words.length) {
      section("WORD CLOUD RESPONSES");
      lines.push(csvRow(["Activity", "Prompt", "Name", "User ID", "Word"]));
      words
        .sort((a, b) => (a.submittedAt || 0) - (b.submittedAt || 0))
        .forEach((w) => {
          lines.push(
            csvRow([
              actLabel(w.activityId),
              questionText(w.activityId, w.qIdx),
              w.name || w.userId,
              w.userId,
              w.text,
            ])
          );
        });
    }

    // --- Pin drop ---
    if ((played.pins || []).length) {
      section("PIN DROP");
      lines.push(
        csvRow(["Activity", "Prompt", "Name", "User ID", "X", "Y", "Correct", "Points", "Time"])
      );
      [...played.pins]
        .sort((a, b) => (a.confirmedAt || 0) - (b.confirmedAt || 0))
        .forEach((p) => {
          lines.push(
            csvRow([
              actLabel(p.activityId),
              questionText(p.activityId, p.qIdx),
              p.name || p.userId,
              p.userId,
              typeof p.x === "number" ? p.x.toFixed(4) : p.x,
              typeof p.y === "number" ? p.y.toFixed(4) : p.y,
              p.correct ? "Yes" : "No",
              p.points || 0,
              ms(p.timeMs),
            ])
          );
        });
    }

    return lines.join("\r\n");
  };

  const handleDownload = async () => {
    if (!currentTheme || !organizationId || !sessionId) {
      Swal.fire("", "No session selected.", "warning");
      return;
    }
    setDownloading(true);
    try {
      const id = liveId(organizationId, sessionId, currentTheme);
      const [played, contentMap] = await Promise.all([
        fetchAllPlayedData(id),
        loadContentMap(),
      ]);
      const totalPlayed =
        (played.participants || []).length +
        (played.responses || []).length +
        (played.votes || []).length +
        (played.words || []).length +
        (played.pins || []).length;
      if (totalPlayed === 0) {
        Swal.fire(
          "Nothing to export yet",
          "No one has played this session, so there's no data to download. The report becomes available once participants join and respond.",
          "info"
        );
        return;
      }
      const csv = buildCsv(played, contentMap);
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${sessionId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to build report", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleReset = async () => {
    if (!currentTheme || !organizationId || !sessionId) {
      Swal.fire("", "No session selected.", "warning");
      return;
    }
    const confirm = await Swal.fire({
      title: "Reset session?",
      html:
        "This permanently deletes <b>all played data</b> for this session — " +
        "participants, quiz answers, poll votes, word cloud responses, pins, and live state.<br/><br/>" +
        "Your activities and questions are <b>kept</b>. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset session",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d23b3b",
    });
    if (!confirm.isConfirmed) return;

    setResetting(true);
    try {
      const id = liveId(organizationId, sessionId, currentTheme);
      const counts = await resetLiveSession(id);
      const total =
        (counts.participants || 0) +
        (counts.responses || 0) +
        (counts.votes || 0) +
        (counts.words || 0) +
        (counts.pins || 0);
      Swal.fire(
        "Session reset",
        `Cleared ${total} played record(s). The session is ready to run fresh.`,
        "success"
      );
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to reset session", "error");
    } finally {
      setResetting(false);
    }
  };

  const isLive = (session?.mode ?? "live") === "live";

  return (
    <>
      <div className="rules-page-new">
        <div className="admin-tabs-holder-v2">
          <div className="tabs-main-title">Review</div>

          <div className="sr-saved-banner">
            <i className="fa-solid fa-circle-check"></i>
            <div>
              <strong>Everything is saved.</strong> Mode, settings and activities are stored as you
              edit them — no separate save needed. Review below or download the report.
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-circle-info"></i> &nbsp; Summary
            </div>
            <div className="sr-grid">
              <div className="sr-cell">
                <span className="sr-k">Title</span>
                <span className="sr-v">{session?.title || "—"}</span>
              </div>
              <div className="sr-cell">
                <span className="sr-k">Mode</span>
                <span className="sr-v">{isLive ? "Live" : "Self-Paced"}</span>
              </div>
              <div className="sr-cell">
                <span className="sr-k">Scoring</span>
                <span className="sr-v">
                  {(session?.scoring_mode ?? "custom") === "auto"
                    ? "Auto (time-based)"
                    : `Custom — ${session?.point_value ?? "—"} / correct`}
                </span>
              </div>
              <div className="sr-cell">
                <span className="sr-k">Activities</span>
                <span className="sr-v">{activities.length}</span>
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-list-ol"></i> &nbsp; Activities
            </div>
            {activities.length === 0 ? (
              <div className="code">No activities added.</div>
            ) : (
              <ol className="sr-activity-list">
                {activities.map((a) => (
                  <li key={a.id}>
                    <span className={`ab-type ab-type-${a.type}`}>
                      {TYPE_LABEL[a.type] || a.type}
                    </span>
                    <span className="sr-act-title">{a.title || "(untitled)"}</span>
                    <span className="sr-act-timer">{a.timer_seconds}s</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="admin-tab-container sr-download-box">
            <div className="admin-tab-title">
              <i className="fa-solid fa-file-csv"></i> &nbsp; Session Report
            </div>
            <p className="code">
              Download a CSV of everything played in this session — the leaderboard plus every
              quiz answer, poll vote, word cloud response and pin drop, with readable question and
              option labels. The report reads live data, so run it after a session (or mid-session
              for a snapshot).
            </p>
            <button
              className="save-and-continue"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Preparing report..." : "Download Report (CSV)"}
            </button>
          </div>

          <div className="admin-tab-container sr-danger-box">
            <div className="admin-tab-title sr-danger-title">
              <i className="fa-solid fa-triangle-exclamation"></i> &nbsp; Reset Session
            </div>
            <p className="code">
              Delete all played data (participants, answers, votes, word cloud responses, pins,
              and live state) so the session can be run fresh. Activities and questions are kept.
              This cannot be undone — download the report first if you need it.
            </p>
            <button className="sr-reset-btn" onClick={handleReset} disabled={resetting}>
              {resetting ? "Resetting..." : "Reset Session Data"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SessionReport;
